import _ from "lodash";
import {
  SELECT_EMPTY_VALUE,
  CHARTS_AND_TYPES_MAPPING,
  CHARTS_TYPES,
  CHARTS_AND_TYPES_AND_LAYERS_MAPPING,
  CHARTS_OVERLAY_COMBO,
  CHARTS,
  CHART_LAYER_TREND_LINES,
  ZONES,
  GRANULARITY_YEAR,
  GRANULARITY_DAY,
  GRANULARITY_MONTH,
  GRANULARITY_WEEK,
  GRANULARITY_QUARTER,
  GRANULARITIES
} from "../managers/constants";
import dateTimeManager from "../managers/datetime";

const service = (() => {
  // Get a list of Chart Types mapped to Chart
  // Ex: Scatter Plot and Histogram are mapped to Cost To Trade
  const getApplicableChartTypesByChart = (whichChart) => {
    if (whichChart === SELECT_EMPTY_VALUE) return [];
    let applicableChartTypes = [];
    let len = CHARTS_AND_TYPES_MAPPING[whichChart].length;
    for (let i = 0; i < len; i++) {
      applicableChartTypes.push(_.find(CHARTS_TYPES, { Code: CHARTS_AND_TYPES_MAPPING[whichChart][i] }));
    }
    return applicableChartTypes;
  };

  // Get applicable Chart Layers mapped to Chart and Chart Type
  // Only those which can be preselected on the screen
  // Ex: Trend Lines, Mid Price Line
  const getApplicableChartLayers = (primaryChart, primaryChartType, comparisonChart, comparisonChartType) => {
    let mappingObj = undefined;
    if (comparisonChart && comparisonChartType && comparisonChart !== SELECT_EMPTY_VALUE) {
      mappingObj =
        _.find(CHARTS_AND_TYPES_AND_LAYERS_MAPPING, {
          Chart: primaryChart,
          Type: primaryChartType,
          OverlayChart: comparisonChart,
          OverlayType: comparisonChartType
        }) ||
        _.find(CHARTS_AND_TYPES_AND_LAYERS_MAPPING, {
          Chart: comparisonChart,
          Type: comparisonChartType,
          OverlayChart: primaryChart,
          OverlayType: primaryChartType
        });
    } else {
      mappingObj = _.find(CHARTS_AND_TYPES_AND_LAYERS_MAPPING, { Chart: primaryChart, Type: primaryChartType });
    }

    let selectiveLayers = mappingObj ? _.concat(mappingObj.Checkboxes || [], mappingObj.Dropdown ? [mappingObj.Dropdown[0]] : []) : [];

    // Do not show trend lines layer on initial load of the chart
    selectiveLayers = _.pull(selectiveLayers, CHART_LAYER_TREND_LINES);

    return { mapping: mappingObj, selectiveLayers };
  };

  const getApplicableComparisonChartsByPrimaryChart = (primaryChart) => {
    let comparisonCharts = [];
    let overlayCombo = _.find(CHARTS_OVERLAY_COMBO, { Chart: primaryChart });
    if (overlayCombo && overlayCombo.Combo && overlayCombo.Combo.length) {
      let len = overlayCombo.Combo.length;
      for (let index = 0; index < len; index++) {
        comparisonCharts.push(_.find(CHARTS, { Code: overlayCombo.Combo[index] }));
      }
    }
    return comparisonCharts;
  };

  const getEnabledDisabledGranularities = (selectedDateRangeFrom, selectedDateRangeTo, selectedZone) => {
    // Enable/disable granularities based on selected date range
    let granularities = [...GRANULARITIES];
    let offset = _.find(ZONES, { Code: selectedZone }).Offset;
    selectedDateRangeFrom = dateTimeManager.getEarlyMorning(selectedDateRangeFrom, offset);
    selectedDateRangeTo = dateTimeManager.getLateNight(selectedDateRangeTo, offset);
    let params = {
      fromTimestamp: selectedDateRangeFrom.unix(),
      toTimestamp: selectedDateRangeTo.unix(),
      offset
    };
    let enabledGranularities = [];

    if (checkIfWithinGranularityRange({ ...params, granularity: GRANULARITY_WEEK })) {
      enabledGranularities = [GRANULARITY_DAY];
    } else if (checkIfWithinGranularityRange({ ...params, granularity: GRANULARITY_MONTH })) {
      enabledGranularities = [GRANULARITY_DAY, GRANULARITY_WEEK];
    } else if (checkIfWithinGranularityRange({ ...params, granularity: GRANULARITY_QUARTER })) {
      enabledGranularities = [GRANULARITY_DAY, GRANULARITY_WEEK, GRANULARITY_MONTH];
    } else if (checkIfWithinGranularityRange({ ...params, granularity: GRANULARITY_YEAR })) {
      enabledGranularities = [GRANULARITY_DAY, GRANULARITY_WEEK, GRANULARITY_MONTH, GRANULARITY_QUARTER];
    } else {
      enabledGranularities = [GRANULARITY_DAY, GRANULARITY_WEEK, GRANULARITY_MONTH, GRANULARITY_QUARTER, GRANULARITY_YEAR];
    }

    granularities.map((granularity) => {
      granularity.isEnabled = _.includes(enabledGranularities, granularity.Code) ? true : false;
    });

    return granularities;
  };

  const getNewSelectedGranularityBasedOnEnabledDisabledState = (granularities, selectedGranularity) => {
    let selectedGranularityObj = _.find(granularities, { Code: selectedGranularity });
    return !selectedGranularityObj.isEnabled ? GRANULARITY_DAY : selectedGranularity;
  };

  const checkIfWithinGranularityRange = (params) => {
    switch (params.granularity) {
      case GRANULARITY_DAY:
        return dateTimeManager.getWholeDate(params.fromTimestamp, params.offset) === dateTimeManager.getWholeDate(params.toTimestamp, params.offset);
      case GRANULARITY_WEEK:
        return (
          dateTimeManager.getIsoWeekOfYear(params.fromTimestamp, params.offset) ===
            dateTimeManager.getIsoWeekOfYear(params.toTimestamp, params.offset) &&
          dateTimeManager.getIsoWeekYear(params.fromTimestamp, params.offset) === dateTimeManager.getIsoWeekYear(params.toTimestamp, params.offset)
        );
      case GRANULARITY_MONTH:
        return (
          dateTimeManager.getMonth(params.fromTimestamp, params.offset) === dateTimeManager.getMonth(params.toTimestamp, params.offset) &&
          dateTimeManager.getYear(params.fromTimestamp, params.offset) === dateTimeManager.getYear(params.toTimestamp, params.offset)
        );
      case GRANULARITY_QUARTER:
        return (
          dateTimeManager.getQuarter(params.fromTimestamp, params.offset) === dateTimeManager.getQuarter(params.toTimestamp, params.offset) &&
          dateTimeManager.getYear(params.fromTimestamp, params.offset) === dateTimeManager.getYear(params.toTimestamp, params.offset)
        );
      case GRANULARITY_YEAR:
        return dateTimeManager.getYear(params.fromTimestamp, params.offset) === dateTimeManager.getYear(params.toTimestamp, params.offset);
    }
  };

  return {
    getApplicableChartTypesByChart,
    getApplicableChartLayers,
    getApplicableComparisonChartsByPrimaryChart,
    getEnabledDisabledGranularities,
    checkIfWithinGranularityRange,
    getNewSelectedGranularityBasedOnEnabledDisabledState
  };
})();

export default service;



// WEBPACK FOOTER //
// ./src/services/transformers/chart-meta-data.js